import React from 'react';
import { Target, TrendingUp, Shield, Zap } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const OurEdge = () => {
  const advantages = [
    {
      icon: Target,
      title: 'Precision Matching',
      description: 'We use advanced algorithms and human expertise to match the right healthcare professionals with the right positions.',
    },
    {
      icon: TrendingUp,
      title: 'Continuous Growth',
      description: 'Our commitment to continuous improvement ensures we stay ahead of industry trends and provide cutting-edge solutions.',
    },
    {
      icon: Shield,
      title: 'Rigorous Vetting',
      description: 'Every healthcare professional in our network undergoes comprehensive background checks and credential verification.',
    },
    {
      icon: Zap,
      title: 'Rapid Deployment',
      description: 'Our streamlined processes enable us to quickly fill critical staffing needs without compromising quality.',
    },
  ];

  return (
    <div className="our-edge-page">
      <PageMeta
        title="Our Edge"
        description="BluekraftMed's competitive edge: precision matching, rigorous vetting, rapid deployment, and continuous growth in healthcare staffing."
        path="/our-edge"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Our Competitive Edge</h1>
            <p className="text-xl leading-relaxed">
              What sets BluekraftMed apart in healthcare staffing excellence
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-6 text-center">Why Healthcare Facilities Choose Us</h2>
            <p className="text-gray-600 text-lg text-center leading-relaxed">
              We combine innovative technology with human expertise to deliver unmatched healthcare staffing solutions. Our approach ensures that every placement is a perfect fit for both the facility and the healthcare professional.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {advantages.map((advantage, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardHeader>
                  <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                    <advantage.icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <CardTitle className="text-2xl">{advantage.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-lg leading-relaxed">{advantage.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Track Record</h2>
            <p className="text-xl">Numbers that speak for themselves</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">99+</div>
              <div className="text-lg">Expert Professionals</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">37+</div>
              <div className="text-lg">Years of Experience</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">18k</div>
              <div className="text-lg">Satisfied Customers</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">100%</div>
              <div className="text-lg">Commitment to Quality</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">What Makes Us Different</h2>
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="bg-cyan-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Innovative Culture</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    We embrace innovation and continuously seek new ways to improve our services and exceed expectations.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-cyan-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Customer Centricity</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Our clients' needs are at the heart of everything we do. We build lasting relationships based on trust and results.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-cyan-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Optimizing Delivery</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    We streamline processes to ensure fast, efficient delivery of qualified healthcare professionals when you need them most.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-cyan-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Forward Thinking</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    We anticipate industry trends and prepare our network to meet tomorrow's healthcare challenges today.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurEdge;